import type { TooltipProps } from 'recharts';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { formatDate, MONEY_TOKENS, useMoneyFormatter } from '@repo/design-system-kit';
import { useIntl } from 'react-intl';
import type { IndicatorChartData, IndicatorChartRate } from '../indicator-chart/types';
import styles from './indicator-chart-tooltip.module.css';

dayjs.extend(isoWeek);

const UNIT_MAP = {
  daily: 'day',
  weekly: 'week',
  monthly: 'month',
} as const;

function toDateWithFallback(value: unknown, fallback: string): Date {
  if (typeof value === 'string' || typeof value === 'number' || value instanceof Date) {
    return new Date(value);
  }
  return new Date(fallback);
}

export interface IndicatorChartTooltipProps extends TooltipProps<number, string> {
  rate: IndicatorChartRate;
}

function getTitle({
  date,
  rate,
  locale,
  formatMessage,
}: {
  date: Date;
  rate: IndicatorChartRate;
  locale: string;
  formatMessage: (descriptor: { id: string }, values?: Record<string, string>) => string;
}): string {
  if (dayjs().isSame(date, UNIT_MAP[rate])) {
    switch (rate) {
      case 'daily':
        return formatMessage({ id: 'overview.dates.today' });
      case 'weekly':
        return formatMessage({ id: 'overview.dates.current-week' });
      case 'monthly':
        return formatMessage({ id: 'overview.dates.current-month' });
      default:
        return formatMessage({ id: 'overview.dates.today' });
    }
  }

  switch (rate) {
    case 'daily':
      return formatDate({ date, locale, token: 'date-year-l' });
    case 'weekly': {
      const startDay = formatDate({
        date: dayjs(date).startOf('isoWeek').toDate(),
        locale,
        token: 'date-year-l',
      });
      const endDay = formatDate({
        date: dayjs(date).endOf('isoWeek').toDate(),
        locale,
        token: 'date-year-l',
      });
      return formatMessage({ id: 'overview.dates.week-of' }, { startDay, endDay });
    }
    case 'monthly':
      return formatDate({ date, locale, token: 'month-year-l' });
    default:
      return formatDate({ date, locale, token: 'date-year-l' });
  }
}

export function IndicatorChartTooltip({
  active,
  payload,
  label,
  rate,
}: IndicatorChartTooltipProps): React.ReactNode {
  const { formatMessage, locale } = useIntl();
  const { formatMoney } = useMoneyFormatter();
  const payloadData = payload?.[0]?.payload as IndicatorChartData | undefined;

  if (!active || !payloadData) {
    return null;
  }

  const date = toDateWithFallback(label, payloadData.date);
  const isFuture = dayjs(date).isAfter(dayjs());

  const title = getTitle({ date, rate, locale, formatMessage });
  const amountLabel = formatMessage({ id: 'overview.amount' });
  const hasCount = Number.isFinite(payloadData.count);
  const countValue = isFuture ? '--' : payloadData.count;
  const amountValue = isFuture
    ? '--'
    : formatMoney({
        amount: payloadData.value.value,
        currency: payloadData.value.currency,
        token: MONEY_TOKENS.SYMBOL,
      });

  return (
    <div aria-live="assertive" className={styles.wrapper} data-testid="indicator-chart-tooltip">
      <section className={styles.section}>
        <h4 className={styles.sectionTitle} data-test-title>
          {title}
        </h4>
      </section>
      <section className={styles.section}>
        {hasCount ? (
          <div className={styles.sectionItem} data-test-count>
            <div className={styles.sectionItemLabel} data-test-label>
              {formatMessage({ id: 'overview.number-of-transactions' })}
            </div>
            <span className={styles.sectionItemValue} data-test-value>
              {countValue}
            </span>
          </div>
        ) : null}
        <div className={styles.sectionItem} data-test-amount>
          <div className={styles.sectionItemLabel} data-test-label>
            {amountLabel}
          </div>
          <span className={styles.sectionItemValue} data-test-value>
            {amountValue}
          </span>
        </div>
      </section>
    </div>
  );
}
